import React, { useContext, useEffect, useState } from 'react'
import '../styles/dashboard.css'
import AppLayout from '../layouts/AppLayout'
import DataTable from '../components/DataTable'
import { AppContext } from '../contexts/AppContext'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { Link } from 'react-router-dom'
import { CiBoxList } from 'react-icons/ci'
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { WorkTitleCell } from '../components/WorkCover'

const Dashboard = () => {
  const { getdashboard, userToken, getrecentmatches, getCompetitions } =
    useContext(AppContext)
  const [stats, setStats] = useState([])
  const [matches, setMatches] = useState([])
  const [competitions, setCompetitions] = useState([])

  const imageUrl = `${process.env.REACT_APP_STATIC_URL}`

  const fetchDashboard = async () => {
    const response = await getdashboard()
    setStats(response)
  }

  const fetchRecentMatches = async () => {
    if (userToken.type === 'Admin') {
      const response = await getrecentmatches()
      setMatches(response)
    }
  }

  const fetchCompetitions = async () => {
    const response = await getCompetitions()
    setCompetitions(response || [])
  }

  useEffect(() => {
    fetchDashboard()
    fetchCompetitions()
  }, [])


  useEffect(() => {
    fetchRecentMatches()
  }, [userToken])

  const columns = React.useMemo(
    () => [
      {
        Header: 'Work',
        accessor: 'title',
        Cell: ({ row }) => (
          <Link to={`/match-details/${row.original.matchid}`}>
            <WorkTitleCell work={row.original} />
          </Link>
        ),
      },
      { Header: 'Category', accessor: 'compname' },
      { Header: 'Language', accessor: 'language' },
      { Header: 'Period', accessor: 'matchday' },
      {
        Header: 'Monitoring since',
        accessor: 'date',
        Cell: ({ row }) =>
          format(new Date(row.original.date), 'EEE, do MMM yyyy', {
            locale: enUS,
          }),
      },
      { Header: 'Status', accessor: 'status' },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => (
          <div className='flex items-center space-x-2'>
            <Link
              className='block btn-main-alt-inv !py-1 !px-3 !rounded-sm !text-[12px]'
              to={`/match-details/${row.original.matchid}`}
            >
              View Details
            </Link>
          </div>
        ),
      },
    ],
    [],
  )

  const StatBox = ({ value, label, info, link }) => (
    <Link to={link}>
      <section className='!mb-0 !shadow-lg h-full'>
        <div className='hidden md:flex'>
          <span className='p-3 bg-neutral-200 rounded-full'>
            <CiBoxList size={22} />
          </span>
        </div>
        <h6
          className='text-sm text-neutral-500 font-medium mt-0 md:mt-3 mb-1'
          dangerouslySetInnerHTML={{ __html: label }}
        ></h6>
        <h2 className='text-4xl font-bold mb-0 md:mb-1'>{value}</h2>
        {info && (
          <span className='px-2 py-1 bg-neutral-200 text-neutral-500 rounded-sm text-xs font-medium leading-none'>
            &bull;&nbsp; {info}
          </span>
        )}
      </section>
    </Link>
  )
  const CustStatBox = ({ value, img, label, info, link }) => (
    <Link to={link}>
      <section className='!mb-0 !p-5 md:!p-7 !shadow-2xl h-full text-center transition-all duration-300 hover:scale-[1.02] hover:shadow-3xl'>
        {img && (
          <img
            className='h-10 md:h-20 mx-auto'
            src={img}
          />
        )}
        <h6
          className='text-2xl md:text-4xl lg:text-5xl text-main font-bold mt-2 md:mt-4 mb-0'
          dangerouslySetInnerHTML={{ __html: label }}
        ></h6>
        {value && <h2 className='text-3xl font-bold'>{value}</h2>}
        {info && (
          <div className='mt-1 md:mt-5 mb-0 md:mb-3 flex justify-center'>
            <div className=' text-neutral-500 rounded-md text-base font-medium'>
              {info}
            </div>
          </div>
        )}
      </section>
    </Link>
  )

  return (
    <AppLayout pageName={'Home'}>
      {stats !== '' ? (
        <>
          {userToken.type === 'Admin' && (
            <>
              <div className='grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 max-sm:mt-1 gap-5'>
                <StatBox
                  link='/all-matches'
                  value={stats.matchestoday}
                  label='Links Found Today'
                />
                <StatBox
                  link='/all-matches'
                  value={stats.matchestomorrow}
                  label='Links Removed Today'
                />
                <StatBox
                  link='/all-matches'
                  value={stats.matches}
                  label='Works Monitored'
                />
                <StatBox
                  link='/all-matches'
                  value={stats.matchesreview}
                  label='Works with Infringements'
                />
                <StatBox
                  link='/all-members'
                  value={stats.members}
                  label='Total Members'
                />
                <StatBox
                  link='/all-members'
                  value={stats.activemembers}
                  label='Active Members'
                />
              </div>
              <section className='mt-5'>
                <h2 className='text-xl mb-3 font-semibold text-neutral-900 tracking-normal'>
                  Works in the Current Period
                </h2>
                <div>
                  <DataTable
                    data={matches}
                    columns={columns}
                  />
                </div>
              </section>
            </>
          )}

          {userToken.type === 'Member' && (
            <>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-sm:mt-1 gap-3'>
                {competitions.map((competition) => {
                  return (
                    <CustStatBox
                      key={competition.id}
                      link={`/matches/${competition.id}`}
                      img={competition.logo ? `${imageUrl}${competition.logo}` : null}
                      label={competition.name}
                      info={`Infringements found and removed for ${competition.name} works`}
                    />
                  )
                })}
                <CustStatBox
                  link='/overview'
                  img='/images/overview.png'
                  label='Overview'
                  info='Infringing links by platform, content type and domain'
                />
                <CustStatBox
                  link='/report'
                  img='/images/reports.png'
                  label='Reports'
                  info='Takedown summaries for each reporting period'
                />
              </div>
            </>
          )}
        </>
      ) : (
        <div className='flex justify-center w-full py-12'>
          <Spinner
            color='#CCCCCC'
            size={24}
          />
        </div>
      )}
    </AppLayout>
  )
}

export default Dashboard
